import { __assign } from "tslib";
import { get, chain } from 'lodash';
import { RegionType, TRANSPARENCY_BREAKPOINT, upgradeColor, } from '@wix/shoutout-email-model-v3';
import classnames from 'classnames';
import { getMessageWidth } from '../../utils/get-message-width';
import * as cssUtils from '../../utils/css-utils';
import { getTextAlign } from '../../utils/get-text-align';
import { adjustImage } from '../../utils/adjust-image';
import { toRgbAsHex } from '../../utils/color';
var NEW_LOGO_IMAGE_SIZES = {
    small: 60,
    medium: 90,
    large: 122,
};
export var getLogoSection = function (_a) {
    var composerData = _a.composerData, css = _a.css, region = _a.region;
    if (region.type !== RegionType.Logo) {
        throw Error('Logo must be passed');
    }
    var logoStyles = region.data.styles;
    var fallbackLogoSettings = composerData.styles.settings.logo;
    var backgroundSize = getBackgroundSize(logoStyles, fallbackLogoSettings);
    var messageContentWidth = getMessageWidth(composerData, css);
    var hasContentPadding = !!css['so-global-region-logo-content'];
    var userOverrides = get(region, 'data.styles');
    var userPaddings = get(region, 'data.styles.region');
    var rules = [
        userOverrides,
        userPaddings,
        css['so-global-region-logo'],
        css['so-global-region'],
    ];
    var margin = cssUtils.margin(rules);
    var padding = cssUtils.padding(rules);
    var regionPaddings = backgroundSize === 'strip'
        ? {
            margin: {
                top: 0,
                left: 0,
                right: 0,
                bottom: margin.bottom,
            },
            padding: hasContentPadding
                ? {
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: padding.bottom,
                }
                : padding,
        }
        : { margin: margin, padding: padding };
    var stripSizeCssClass = cssUtils.getBottomPaddingRestrictionCssClass(rules);
    var mobileCssClass = classnames(cssUtils.getRegionMobileCssClass(rules), stripSizeCssClass);
    if (composerData.styles.v1 && backgroundSize === 'strip') {
        if (margin.left) {
            stripSizeCssClass = classnames(stripSizeCssClass, 'ML16');
            regionPaddings.margin.left = margin.left;
        }
        if (margin.right) {
            stripSizeCssClass = classnames(stripSizeCssClass, 'MR16');
            regionPaddings.margin.right = margin.right;
        }
    }
    if (!hasContentPadding && backgroundSize === 'strip') {
        stripSizeCssClass = cssUtils.getRegionMobileCssClass([
            userOverrides,
            cssUtils.spacingToCssProperties(regionPaddings.margin, regionPaddings.padding),
        ]);
    }
    var logoBackgroundColor = getBackgroundColor(region.data.styles, composerData.styles.settings.logo);
    var align = getTextAlign(logoStyles === null || logoStyles === void 0 ? void 0 : logoStyles.textAlign);
    var logoSizes = NEW_LOGO_IMAGE_SIZES;
    var imageSize = logoSizes[(logoStyles === null || logoStyles === void 0 ? void 0 : logoStyles.imageSize) || 'medium'];
    var fileName = getLogoSrc(region, composerData);
    var contentPaddings = cssUtils.padding([
        css['so-global-region-logo-content'],
    ]);
    // We are doubling the image height to increase the image quality.
    // Browsers and/or email clients are handling image resize better than wix media.
    var h = imageSize * 2;
    var w = messageContentWidth -
        (regionPaddings.margin.left || 0) -
        (regionPaddings.margin.right || 0) -
        (regionPaddings.padding.left || 0) -
        (regionPaddings.padding.right || 0) -
        (contentPaddings.left || 0) -
        (contentPaddings.right || 0);
    // NOTE: Make sure that ImageWithLoader from composer still works if you change this.
    var imageProps = {
        h: h,
        q: 100,
        w: w,
        al: 'c',
        lg: 0,
    };
    var src = adjustImage(fileName, imageProps);
    var srcNonRetina = adjustImage(fileName, __assign(__assign({}, imageProps), { h: imageSize }));
    var columnProps;
    if (hasContentPadding) {
        columnProps = {
            innerBackgroundColor: logoBackgroundColor,
        };
    }
    else {
        columnProps = {
            backgroundColor: logoBackgroundColor,
        };
    }
    return {
        align: align,
        backgroundSize: backgroundSize,
        href: region.data.imageLink,
        imageSize: imageSize,
        src: src,
        srcNonRetina: srcNonRetina,
        outerPadding: regionPaddings.margin,
        innerPadding: regionPaddings.padding,
        columnProps: columnProps,
        cssClass: backgroundSize === 'strip' ? stripSizeCssClass : mobileCssClass,
    };
};
var LogoType;
(function (LogoType) {
    LogoType["custom"] = "custom";
    LogoType["siteLogo"] = "siteLogo";
})(LogoType || (LogoType = {}));
function getLogoSrc(region, composerData) {
    var filename = get(region, 'data.relativeUri') ||
        get(composerData, 'styles.settings.logo.image.url');
    var logoType = get(region, 'data.logoType');
    if (logoType === LogoType.siteLogo) {
        filename = get(region, 'data.siteLogoUrl', filename);
    }
    return filename;
}
function getBackgroundSize(styles, fallbackStyles) {
    return styles.backgroundColorEnabled
        ? styles.backgroundSize || fallbackStyles.backgroundSize || 'box'
        : 'box';
}
export function getLogoType(composerData) {
    var _a;
    return getBackgroundSize(((_a = composerData.regions.filter(function (region) { return region.type === RegionType.Logo; })[0]) === null || _a === void 0 ? void 0 : _a.data.styles) || {}, get(composerData, 'styles.settings.logo') || {});
}
function getBackgroundColor(styles, defaults) {
    if (styles.backgroundColorEnabled) {
        var opacity = chain([styles.opacity, defaults.opacity])
            .map(function (_) { return parseInt(_, 10); })
            .filter(Number.isInteger)
            .first()
            .defaultTo(100)
            .value();
        if (opacity > TRANSPARENCY_BREAKPOINT) {
            return (toRgbAsHex(upgradeColor(styles.backgroundColor || defaults.backgroundColor)) || 'transparent');
        }
    }
    return 'transparent';
}
//# sourceMappingURL=get-logo-section.js.map